import { gql } from "@apollo/client";

export const SUBSCRIBER_AUDIENCE_FIELDS = gql`
  fragment SubscriberAudienceFields on Audience {
    id
    shortCodeConfig {
      helpKeywords
      helpMessage
      optInKeywords
      optInMessage
      optOutKeywords
      optOutMessage
    }
    audienceContactImportJob {
      id
      status
      startedAt
      errorMessage
      args {
        firstNameColumn
        lastNameColumn
        phoneNumberColumn
        silentBackfill
        upload {
          id
          fileName
        }
      }
      result {
        nNewSubscribers
        nExistingSubscribersUpdated
        nOptedOutSubscribers
        nInvalid
        nDuplicates
        nInvalidRows
      }
    }
    ownedPhoneNumbers {
      id
      messageClass
      state
      phoneNumber
    }
  }
`;
export const GET_SUBACCOUNT_SUBSCRIBER_AUDIENCE = gql`
  ${SUBSCRIBER_AUDIENCE_FIELDS}
  query getSubaccountSubscriberAudience($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      id
      subscriberAudience {
        ...SubscriberAudienceFields
      }
      integrations {
        id
        name
        service
        createdAt

        ... on ActBlueWebhookIntegration {
          settings {
            webhookUrl
            subscribeDonors
            sendDonationMessage {
              script
              mediaUpload {
                id
                fileName
                contentType
                publicGetUrl
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_SUBACCOUNT_AUDIENCE_INTEGRATIONS = gql`
  query getSubaccountSubscriberIntegrations($input: GetSubaccountInput!) {
    getSubaccount(input: $input) {
      id
      integrations {
        id
        name
        service
        createdAt

        ... on ActBlueWebhookIntegration {
          settings {
            webhookUrl
            subscribeDonors
            sendDonationMessage {
              script
              mediaUpload {
                id
                fileName
                contentType
                publicGetUrl
              }
            }
          }
        }
      }
    }
  }
`;

const SHORT_CODE_OPT_IN_KEYWORD_FIELDS = gql`
  fragment ShortCodeOptInKeywordFields on ShortCodeOptInKeyword {
    id

    createdAt
    createdByUser {
      id
      fullName
    }

    updatedAt
    updatedByUser {
      id
      fullName
    }

    normalizedKeyword
    additionalWelcomeMessage

    mediaUpload {
      id
      fileName
      contentType
      publicGetUrl
    }
  }
`;

export const LIST_SHORT_CODE_OPT_IN_KEYWORDS = gql`
  ${SHORT_CODE_OPT_IN_KEYWORD_FIELDS}

  query listShortCodeOptInKeywords($input: ListShortCodeOptInKeywordsInput!) {
    listShortCodeOptInKeywords(input: $input) {
      ...ShortCodeOptInKeywordFields
    }
  }
`;

export const CREATE_SHORT_CODE_OPT_IN_KEYWORD = gql`
  ${SHORT_CODE_OPT_IN_KEYWORD_FIELDS}

  mutation createShortCodeOptInKeyword(
    $input: CreateShortCodeOptInKeywordInput!
  ) {
    createShortCodeOptInKeyword(input: $input) {
      ...ShortCodeOptInKeywordFields
    }
  }
`;

export const UPDATE_SHORT_CODE_OPT_IN_KEYWORD = gql`
  ${SHORT_CODE_OPT_IN_KEYWORD_FIELDS}

  mutation updateShortCodeOptInKeyword(
    $input: UpdateShortCodeOptInKeywordInput!
  ) {
    updateShortCodeOptInKeyword(input: $input) {
      ...ShortCodeOptInKeywordFields
    }
  }
`;

export const DELETE_SHORT_CODE_OPT_IN_KEYWORD = gql`
  mutation deleteShortCodeOptInKeyword(
    $input: DeleteShortCodeOptInKeywordInput!
  ) {
    deleteShortCodeOptInKeyword(input: $input)
  }
`;

const SUBSCRIBER_FIELDS = gql`
  fragment SubscriberFields on Subscriber {
    id
    createdAt
    updatedAt
    createdByUser {
      id
      fullName
    }
    phoneNumber
    fields {
      name
      displayName
      type
      value
    }
    optOuts {
      id
      createdAt
      createdByUser {
        id
        fullName
      }
      source {
        type
      }
      deletedAt
      deleteSourceType
      deletedByUser {
        id
        fullName
      }
    }
    threadId
    source
  }
`;

export const LIST_SUBSCRIBERS = gql`
  ${SUBSCRIBER_FIELDS}

  query listSubscribers($input: ListSubscribersInput!) {
    listSubscribers(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const CREATE_SUBSCRIBER = gql`
  ${SUBSCRIBER_FIELDS}

  mutation createSubscriber($input: CreateSubscriberInput!) {
    createSubscriber(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const UNSUBSCRIBE_SUBSCRIBER = gql`
  ${SUBSCRIBER_FIELDS}

  mutation unsubscribeSubscriber($input: UnsubscribeSubscriberInput!) {
    unsubscribeSubscriber(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const RESUBSCRIBE_SUBSCRIBER = gql`
  ${SUBSCRIBER_FIELDS}

  mutation resubscribeSubscriber($input: ResubscribeSubscriberInput!) {
    resubscribeSubscriber(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const UPDATE_SUBSCRIBER = gql`
  ${SUBSCRIBER_FIELDS}

  mutation updateSubscriber($input: UpdateSubscriberInput!) {
    updateSubscriber(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const GET_SUBSCRIBER_SUMMARY = gql`
  query getSubscriberSummary($input: GetSubscriberSummaryInput!) {
    getSubscriberSummary(input: $input) {
      activeSubscribers
      unsubscribedSubscribers
    }
  }
`;

export const GET_SUBSCRIBER = gql`
  ${SUBSCRIBER_FIELDS}

  query getSubscriber($input: GetSubscriberInput!) {
    getSubscriber(input: $input) {
      ...SubscriberFields
    }
  }
`;

export const UPLOAD_SUBSCRIBERS = gql`
  ${SUBSCRIBER_AUDIENCE_FIELDS}
  mutation uploadSubscribers($input: UploadSubscribersInput!) {
    uploadSubscribers(input: $input) {
      ...SubscriberAudienceFields
    }
  }
`;

export const GET_SUBSCRIBER_UPLOAD_INVALID_ROWS_SIGNED_DOWNLOAD_URL = gql`
  query getSubscriberUploadInvalidRowsSignedDownloadUrl(
    $input: GetSubscriberUploadInvalidRowsSignedDownloadUrlInput!
  ) {
    getSubscriberUploadInvalidRowsSignedDownloadUrl(input: $input)
  }
`;

export const GET_SUBSCRIBER_BY_AUDIENCE_CONTACT = gql`
  ${SUBSCRIBER_FIELDS}

  query getSubscriberByAudienceContact(
    $input: GetSubscriberByAudienceContactInput!
  ) {
    getSubscriberByAudienceContact(input: $input) {
      ...SubscriberFields
    }
  }
`;
